import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Message, SendMessageOptions, ChatWidgetConfig } from '../types';
import { fetchConfig, sendMessage, WebSdkConfigResponse } from '../api/api-client';

interface ChatContextType {
  messages: Message[];
  isLoading: boolean;
  serverConfig: WebSdkConfigResponse | null;
  sendMessage: (options: SendMessageOptions) => Promise<void>;
  clearMessages: () => void;
}

// Create context with default values
const ChatContext = createContext<ChatContextType>({
  messages: [],
  isLoading: false,
  serverConfig: null,
  sendMessage: async () => {},
  clearMessages: () => {},
});

export const useChatContext = () => useContext(ChatContext);

interface ChatProviderProps {
  children: React.ReactNode;
  config: ChatWidgetConfig;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children, config }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [serverConfig, setServerConfig] = useState<WebSdkConfigResponse | null>(null);
  const [configFetched, setConfigFetched] = useState(false);
  
  // Function to add message to state
  const addMessage = useCallback((message: Message) => {
    setMessages(prevMessages => [...prevMessages, message]);
  }, []);
  
  // Function to update the last message in the list
  const updateLastMessage = useCallback((updates: Partial<Message>) => {
    setMessages(prevMessages => {
      if (prevMessages.length === 0) return prevMessages;
      
      const messagesCopy = [...prevMessages];
      const lastIndex = messagesCopy.length - 1;
      messagesCopy[lastIndex] = { ...messagesCopy[lastIndex], ...updates };
      
      return messagesCopy;
    });
  }, []);
  
  // Function to clear all messages
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);
  
  // Send message to the API
  const handleSendMessage = useCallback(async (options: SendMessageOptions) => {
    const { message, resetConversation = false, silent = false } = options;
    
    // Add user message to the chat (if not silent)
    if (!silent && message.trim() !== '') {
      addMessage({
        id: uuidv4(),
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      });
    }
    
    // Add loading message for assistant
    addMessage({
      id: uuidv4(),
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isLoading: true,
    });
    
    setIsLoading(true);
    
    try {
      // Call the API - note that we don't pass userId anymore as the API client will generate a random ID
      const response = await sendMessage({
        message,
        clientId: config.clientId,
        assistantId: config.assistantId,
        region: config.region,
        resetConversation,
      });
      
      // Create metadata object if additional data exists
      const metadata: Message['metadata'] = {};
      
      if (response.total_response_time) {
        metadata.responseTime = response.total_response_time;
      }
      
      if (response.current_module) {
        metadata.module = response.current_module;
      }
      
      if (response.message_type) {
        metadata.messageType = response.message_type;
      }
      
      // Update the last message with the response
      updateLastMessage({ 
        content: response.output_message, 
        isLoading: false,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined 
      });
    } catch (error) {
      // Update with error message
      updateLastMessage({
        content: 'Sorry, there was an error processing your request. Please try again.',
        isLoading: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, [addMessage, updateLastMessage, config]);
  
  // Fetch configuration on initial mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        // Ensure we have the client ID and assistant ID
        const clientId = config.clientId;
        const assistantId = config.assistantId;
        
        if (!clientId || !assistantId) {
          console.error('Missing required configuration parameters:', config);
          return;
        }
        
        console.log('Loading config with params:', {
          clientId,
          assistantId,
          region: config.region
        });
        
        const configData = await fetchConfig(
          clientId, 
          assistantId, 
          config.region
        );
        setServerConfig(configData);
        setConfigFetched(true);
      } catch (error) {
        console.error('Failed to load config:', error);
      }
    };
    
    loadConfig();
  }, [config.clientId, config.assistantId, config.region]);
  
  // Send empty message once config is fetched to get the initial response
  useEffect(() => {
    if (configFetched && config.autoSendFirstMessage !== false && messages.length === 0) {
      // Use silent mode to not show the empty message
      handleSendMessage({ 
        message: "", 
        resetConversation: true,
        silent: true 
      });
    }
  }, [configFetched, messages.length, handleSendMessage, config.autoSendFirstMessage]);
  
  const contextValue = {
    messages,
    isLoading,
    serverConfig,
    sendMessage: handleSendMessage,
    clearMessages,
  };
  
  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
}; 